<template>
  <div class="matrix-wrapper">
    <div class="matrix-header">
      <h3>Flödesmatris</h3>
      <p>Antal ärenden per par (från&ndash;till). Mörkare = fler flytt.</p>
    </div>
    <div class="matrix-scroll">
      <table class="matrix-table">
        <thead>
          <tr>
            <th class="matrix-corner">Från &#8595; Till &#8594;</th>
            <th
              v-for="city in sortedCities"
              :key="'col-' + city.id"
              class="matrix-col-header"
            >
              <span>{{ city.label }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="from in sortedCities" :key="'row-' + from.id">
            <td class="matrix-row-header">{{ from.label }}</td>
            <td
              v-for="to in sortedCities"
              :key="'cell-' + from.id + '-' + to.id"
              class="matrix-cell"
              :style="cellStyle(from.id, to.id)"
              @click="onCellClick(from.id, to.id)"
            >
              <template v-if="from.id === to.id">&mdash;</template>
              <template v-else-if="getFlowCount(from.id, to.id) > 0">
                {{ getFlowCount(from.id, to.id) }}
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="matrix-highlights">
      <div class="highlight-card">
        <div class="highlight-label">Starkaste flöde</div>
        <div class="highlight-value">{{ strongestFlow.label }}</div>
        <div class="highlight-sub">{{ strongestFlow.count }} ärenden</div>
      </div>
      <div class="highlight-card">
        <div class="highlight-label">Mest aktiva ort</div>
        <div class="highlight-value">{{ mostActiveNode.label }}</div>
        <div class="highlight-sub">
          {{ mostActiveNode.relations }} flödesrelationer
        </div>
      </div>
      <div class="highlight-card">
        <div class="highlight-label">Längsta flyttavstånd</div>
        <div class="highlight-value">{{ longestDistance.label }}</div>
        <div class="highlight-sub">{{ longestDistance.km }} km</div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    nodes: { type: Array, required: true },
    rawFlows: { type: Array, required: true }
  },
  computed: {
    sortedCities() {
      return [...this.nodes]
        .sort((a, b) => {
          const totalA = a.outgoing + a.incoming;
          const totalB = b.outgoing + b.incoming;
          return totalB - totalA;
        })
        .slice(0, 12);
    },
    maxFlow() {
      return Math.max(...this.rawFlows.map(f => f.count), 1);
    },
    strongestFlow() {
      if (!this.rawFlows.length) return { label: '-', count: 0 };
      const top = [...this.rawFlows].sort((a, b) => b.count - a.count)[0];
      return {
        label: `${this.shorten(top.from)} \u2192 ${this.shorten(top.to)}`,
        count: top.count
      };
    },
    mostActiveNode() {
      if (!this.nodes.length) return { label: '-', relations: 0 };
      const relationCount = {};
      this.rawFlows.forEach(f => {
        relationCount[f.from] = (relationCount[f.from] || 0) + 1;
        relationCount[f.to] = (relationCount[f.to] || 0) + 1;
      });
      const top = Object.entries(relationCount).sort((a, b) => b[1] - a[1])[0];
      return { label: top[0], relations: top[1] };
    },
    longestDistance() {
      if (!this.rawFlows.length || !this.nodes.length)
        return { label: '-', km: 0 };

      let maxDist = 0;
      let maxFlow = null;

      this.rawFlows.forEach(f => {
        const from = this.nodes.find(n => n.id === f.from);
        const to = this.nodes.find(n => n.id === f.to);
        if (!from || !to) return;
        const dist = this.haversine(from.lat, from.lng, to.lat, to.lng);
        if (dist > maxDist) {
          maxDist = dist;
          maxFlow = f;
        }
      });

      return {
        label: maxFlow
          ? `${this.shorten(maxFlow.from)} \u2192 ${this.shorten(maxFlow.to)}`
          : '-',
        km: Math.round(maxDist)
      };
    }
  },
  methods: {
    getFlowCount(fromId, toId) {
      const f = this.rawFlows.find(fl => fl.from === fromId && fl.to === toId);
      return f ? f.count : 0;
    },

    cellStyle(fromId, toId) {
      if (fromId === toId) {
        return { background: '#E8E2D6', cursor: 'default' };
      }
      const count = this.getFlowCount(fromId, toId);
      if (count === 0) return {};
      const intensity = count / this.maxFlow;
      const alpha = intensity * 0.8 + 0.05;
      return {
        background: `rgba(42, 123, 111, ${alpha})`,
        cursor: 'pointer',
        fontWeight: count > this.maxFlow * 0.5 ? '700' : '400',
        color: count > this.maxFlow * 0.6 ? '#FDFBF7' : '#1A1209'
      };
    },

    onCellClick(fromId, toId) {
      if (fromId === toId) return;
      const count = this.getFlowCount(fromId, toId);
      if (!count) return;
    },

    shorten(name) {
      return name.length > 10 ? name.substring(0, 9) + '.' : name;
    },

    haversine(lat1, lng1, lat2, lng2) {
      const R = 6371;
      const dLat = ((lat2 - lat1) * Math.PI) / 180;
      const dLng = ((lng2 - lng1) * Math.PI) / 180;
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos((lat1 * Math.PI) / 180) *
          Math.cos((lat2 * Math.PI) / 180) *
          Math.sin(dLng / 2) *
          Math.sin(dLng / 2);
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }
  }
};
</script>

<style scoped>
.matrix-wrapper {
  padding: 24px 28px;
}

.matrix-header h3 {
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1209;
  margin: 0 0 4px 0;
}

.matrix-header p {
  font-size: 1.1rem;
  color: #7a6e5f;
  margin: 0 0 18px 0;
}

.matrix-scroll {
  overflow-x: auto;
  margin-bottom: 24px;
}

.matrix-table {
  border-collapse: collapse;
  font-size: 1.1rem;
  width: 100%;
}

.matrix-corner {
  padding: 6px 8px;
  color: #7a6e5f;
  font-weight: 600;
  text-align: left;
  font-size: 1rem;
}

.matrix-col-header {
  padding: 4px 6px;
  color: #7a6e5f;
  font-weight: 600;
  font-size: 0.9rem;
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  max-width: 24px;
  white-space: nowrap;
}

.matrix-row-header {
  padding: 4px 8px;
  color: #1a1209;
  font-weight: 600;
  font-size: 1rem;
  white-space: nowrap;
}

.matrix-cell {
  padding: 2px;
  text-align: center;
  border-radius: 3px;
  font-size: 1rem;
  min-width: 32px;
  height: 32px;
  color: #e8e2d6;
}

.matrix-highlights {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.highlight-card {
  background: #fdfbf7;
  border-radius: 10px;
  border: 1px solid #d6cebf;
  padding: 16px 18px;
}

.highlight-label {
  font-size: 1rem;
  color: #7a6e5f;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 6px;
}

.highlight-value {
  font-size: 1.6rem;
  font-weight: 700;
  color: #1a1209;
  margin-bottom: 3px;
}

.highlight-sub {
  font-size: 1.1rem;
  color: #7a6e5f;
}
</style>
